import { Currency, CurrencyAmount, Ether, JSBI, Pair, Percent, Price, Token } from '@xatadev/sdk'
import { BigNumber as JSBigNumber } from 'bignumber.js'
// import { BigNumber } from 'ethers'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field } from 'state/AddLiquidity/actions'
import { useAddLiquidityState } from 'state/AddLiquidity/hooks'

// import { useIsExpertMode, useUserFeeTokens, useUserUseCustomFeeToken } from 'state/user/hooks'
import { PairState, usePair } from '../../data/Reserves'
import { useTotalSupply } from '../../data/TotalSupply'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
// import useEnvironment from '../../hooks/useEnvironment'
// import useEnvironment from 'hooks/useEnvironment'
// import useGasLimit, { getDefaultGasLimit } from '../../hooks/useGasLimit'
import { wrappedCurrency, wrappedCurrencyAmount } from '../../utils/wrappedCurrency'
// import { /*calculateFeeOnToken,*/ GAS_LIMIT } from '../../constants/price/price'
// import { addLiquitityFeeInTokens, feeTokenSymbol } from '../fee/actions'
import { AppDispatch, AppState } from '../index'
// import { WrappedTokenInfo } from '../lists/hooks'
import { tryParseAmount } from '../swap/hooks'
import { useCurrencyBalances } from '../wallet/hooks'
import { typeInput } from './actions'

const ZERO = JSBI.BigInt(0)

export function useMintState(): AppState['mint'] {
  return useSelector<AppState, AppState['mint']>(state => state.mint)
}

export function useMintActionHandlers(
  noLiquidity: boolean | undefined
): {
  onFieldAInput: (typedValue: string) => void
  onFieldBInput: (typedValue: string) => void
} {
  const dispatch = useDispatch<AppDispatch>()

  const onFieldAInput = useCallback(
    (typedValue: string) => {
      dispatch(
        typeInput({ field: Field.CURRENCY_A, typedValue, noLiquidity: noLiquidity === true })
      )
    },
    [dispatch, noLiquidity]
  )
  const onFieldBInput = useCallback(
    (typedValue: string) => {
      dispatch(
        typeInput({ field: Field.CURRENCY_B, typedValue, noLiquidity: noLiquidity === true })
      )
    },
    [dispatch, noLiquidity]
  )

  return {
    onFieldAInput,
    onFieldBInput
  }
}

export function useDerivedMintInfo(): {
  dependentField: Field
  currencies: { [field in Field]?: Currency }
  pair?: Pair | null
  pairState: PairState
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  // parsedOriginalAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  price?: Price<Currency, Currency>
  noLiquidity?: boolean
  liquidityMinted?: CurrencyAmount<Currency>
  poolTokenPercentage?: Percent
  error?: string
  // feeCurrency: Token | Currency | null
} {
  const { account, chainId } = useActiveWeb3React()

  // const environment = useEnvironment()

  const { independentField, typedValue, otherTypedValue } = useMintState()
  const {
    [Field.CURRENCY_A]: { currencyId: currencyIdA },
    [Field.CURRENCY_B]: { currencyId: currencyIdB }
  } = useAddLiquidityState()

  const currencyA = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)

  const dependentField = independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A

  // const wrappedTokenA = currencyA as WrappedTokenInfo

  // tokens
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA ?? undefined,
      [Field.CURRENCY_B]: currencyB ?? undefined
    }),
    [currencyA, currencyB]
  )

  // pair
  const [pairState, pair] = usePair(currencies[Field.CURRENCY_A], currencies[Field.CURRENCY_B])
  const totalSupply = useTotalSupply(pair?.liquidityToken)

  const noLiquidity: boolean =
    pairState === PairState.NOT_EXISTS ||
    Boolean(totalSupply && JSBI.equal(totalSupply.numerator, ZERO))

  // balances
  const balances = useCurrencyBalances(account ?? undefined, [
    currencies[Field.CURRENCY_A],
    currencies[Field.CURRENCY_B]
  ])
  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]: balances[0],
    [Field.CURRENCY_B]: balances[1]
  }

  // amounts
  const independentAmount: CurrencyAmount<Currency> | undefined = tryParseAmount(
    typedValue,
    currencies[independentField]
  )
  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    if (noLiquidity) {
      if (otherTypedValue && currencies[dependentField]) {
        return tryParseAmount(otherTypedValue, currencies[dependentField])
      }
      return undefined
    } else if (independentAmount && currencyA && currencyB) {
      // we wrap the currencies just to get the price in terms of the other token
      const wrappedIndependentAmount = wrappedCurrencyAmount(independentAmount, chainId)
      const [tokenA, tokenB] = [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId)
      ]
      if (tokenA && tokenB && wrappedIndependentAmount && pair) {
        const dependentCurrency = dependentField === Field.CURRENCY_B ? currencyB : currencyA
        const dependentTokenAmount =
          dependentField === Field.CURRENCY_B
            ? pair.priceOf(tokenA).quote(wrappedIndependentAmount as CurrencyAmount<Token>)
            : pair.priceOf(tokenB).quote(wrappedIndependentAmount as CurrencyAmount<Token>)

        return dependentCurrency === Ether.onChain(chainId!)
          ? CurrencyAmount.fromRawAmount(dependentCurrency, dependentTokenAmount.numerator)
          : dependentTokenAmount
      }
      return undefined
    } else {
      return undefined
    }
  }, [
    noLiquidity,
    otherTypedValue,
    dependentField,
    independentAmount,
    chainId,
    pair,
    currencyB,
    currencyA,
    currencies
  ])

  // FIXME: The code below makes the dependencies of useMemo Hook (at line 173) change on every render
  // const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = {
  //   [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
  //   [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentAmount : independentAmount
  // }
  // FIXME: This code is a quick fix for the issue above
  const parsedAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A ? independentAmount : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A ? dependentAmount : independentAmount
    }
  }, [dependentAmount, independentAmount, independentField])

  // const independentOriginalAmount: CurrencyAmount<Currency> | undefined = tryParseAmount(
  //   typedValue,
  //   currencies[independentField]
  // )
  // const dependentTokenAmount = dependentAmount as CurrencyAmount<Currency>
  // const dependentToken = currencies[dependentField] as Token
  // const dependentOriginalAmount: CurrencyAmount<Currency> | undefined =
  //   dependentTokenAmount === undefined || dependentToken === undefined
  //     ? undefined
  //     : CurrencyAmount.fromRawAmount(dependentToken, dependentTokenAmount.numerator)

  // const parsedOriginalAmounts: { [field in Field]: CurrencyAmount<Currency> | undefined } = {
  //   [Field.CURRENCY_A]: independentField === Field.CURRENCY_A ? independentOriginalAmount : dependentOriginalAmount,
  //   [Field.CURRENCY_B]: independentField === Field.CURRENCY_A ? dependentOriginalAmount : independentOriginalAmount
  // }

  const price = useMemo(() => {
    if (noLiquidity) {
      const {
        [Field.CURRENCY_A]: currencyAAmount,
        [Field.CURRENCY_B]: currencyBAmount
      } = parsedAmounts
      if (currencyAAmount && currencyBAmount) {
        return new Price(
          currencyAAmount.currency,
          currencyBAmount.currency,
          currencyAAmount.numerator,
          currencyBAmount.numerator
        )
      }
      return undefined
    } else if (currencyA) {
      const wrappedCurrencyA = wrappedCurrency(currencyA, chainId)
      return pair && wrappedCurrencyA ? pair.priceOf(wrappedCurrencyA) : undefined
    } else {
      return undefined
    }
  }, [chainId, noLiquidity, pair, parsedAmounts, currencyA])

  // liquidity minted
  const liquidityMinted = useMemo(() => {
    const {
      [Field.CURRENCY_A]: currencyAAmount,
      [Field.CURRENCY_B]: currencyBAmount
    } = parsedAmounts
    const [tokenAmountA, tokenAmountB] = [
      wrappedCurrencyAmount(currencyAAmount, chainId),
      wrappedCurrencyAmount(currencyBAmount, chainId)
    ]
    if (pair && totalSupply && tokenAmountA && tokenAmountB) {
      return pair.getLiquidityMinted(totalSupply, tokenAmountA, tokenAmountB)
    } else {
      return undefined
    }
  }, [parsedAmounts, chainId, pair, totalSupply])

  const poolTokenPercentage = useMemo(() => {
    if (liquidityMinted && totalSupply) {
      return new Percent(liquidityMinted.numerator, totalSupply.add(liquidityMinted).numerator)
    } else {
      return undefined
    }
  }, [liquidityMinted, totalSupply])

  let error: string | undefined
  if (!account) {
    error = 'Connect Wallet'
  }

  if (pairState === PairState.INVALID) {
    error = error ?? 'Invalid pair'
  }

  if (!parsedAmounts[Field.CURRENCY_A] || !parsedAmounts[Field.CURRENCY_B]) {
    error = error ?? 'Enter an amount'
  }

  const { [Field.CURRENCY_A]: currencyAAmount, [Field.CURRENCY_B]: currencyBAmount } = parsedAmounts

  const addLiquitityFeeStr = useSelector<AppState, AppState['fee']['addLiquitityFeeInTokens']>(
    state => state.fee.addLiquitityFeeInTokens
  )
  const addLiquitityFee =
    addLiquitityFeeStr === undefined
      ? undefined
      : currencies[Field.CURRENCY_A] === undefined
      ? undefined
      : new JSBigNumber(addLiquitityFeeStr).div(
          new JSBigNumber(10).pow(currencies[Field.CURRENCY_A]!.decimals).toString()
        )

  // const isExpertMode = useIsExpertMode()
  // const [userLiquidityGasLimit] = useUserLiquidityGasLimit()
  // const { gasLimit } = useGasLimit()

  const swapFeeCurrencySymbol = useSelector<AppState, AppState['fee']['feeTokenSymbol']>(
    state => state.fee.feeTokenSymbol
  )

  if (currencyAAmount && currencyBalances && currencyBalances?.[Field.CURRENCY_A]) {
    if (currencyBalances?.[Field.CURRENCY_A]?.lessThan(currencyAAmount)) {
      error = 'Insufficient ' + currencies[Field.CURRENCY_A]?.symbol + ' balance'
    } else if (
      addLiquitityFee !== undefined &&
      currencies[Field.CURRENCY_A]?.symbol === swapFeeCurrencySymbol &&
      addLiquitityFee
        .plus(new JSBigNumber(currencyAAmount.toSignificant(currencyAAmount.currency.decimals)))
        .gt(
          new JSBigNumber(
            currencyBalances?.[Field.CURRENCY_A]!.toSignificant(
              currencyBalances?.[Field.CURRENCY_A]?.currency.decimals
            )
          )
        )
    ) {
      error = 'Insufficient ' + currencies[Field.CURRENCY_A]?.symbol + ' balance for gas fee'
    }
  }

  if (currencyBAmount && currencyBalances?.[Field.CURRENCY_B]?.lessThan(currencyBAmount)) {
    error = 'Insufficient ' + currencies[Field.CURRENCY_B]?.symbol + ' balance'
  }

  // const dispatch = useDispatch<AppDispatch>()

  // const [userUseCustomFeeToken] = useUserUseCustomFeeToken()
  // const [userFeeTokens] = useUserFeeTokens()
  // const userFeeCurrency = useCurrency(chainId ? userFeeTokens[chainId] : undefined)

  // const [feeCurrency, setFeeCurrency] = useState<Token | Currency | null>(null)

  // library?.getGasPrice().then(async (value: BigNumber) => {
  //   if (chainId !== undefined && wrappedTokenA && value !== undefined) {
  //     // const userGasLimit = isExpertMode
  //     //   ? userLiquidityGasLimit
  //     //   : pairState === PairState.NOT_EXISTS
  //     //   ? environment === Environment.STAGING
  //     //     ? GAS_LIMIT[Environment.STAGING].createPair
  //     //     : CREATE_PAIR_GAS_LIMIT
  //     //   : ADD_LIQUIDITY_GAS_LIMIT
  //     const userGasLimit = isExpertMode
  //       ? gasLimit[GasLimit.LIQUIDITY_ADD]
  //       : pairState === PairState.NOT_EXISTS
  //       ? getDefaultGasLimit(chainId, GasLimit.CREATE_PAIR)
  //       : getDefaultGasLimit(chainId, GasLimit.LIQUIDITY_ADD)
  //
  //     const txnFee = value.mul(userGasLimit)
  //     // const decimals = BigNumber.from(wrappedTokenA.decimals)
  //     // const decimals =
  //     //   userUseCustomFeeToken && userFeeCurrency
  //     //     ? BigNumber.from(userFeeCurrency.decimals)
  //     //     : BigNumber.from(wrappedTokenA.decimals)
  //
  //     let decimals: BigNumber = BigNumber.from(0)
  //     let feeCurrency: Token | Currency | undefined = undefined
  //     let feeCurrencyId: string = ''
  //     if (!userUseCustomFeeToken && pair && pair.liquidityToken.address) {
  //       // Check if the pair contract addresss is exist on coingecko
  //       try {
  //         const response = await fetchPrice(
  //           chainId!,
  //           pair.liquidityToken.address.toLowerCase(),
  //           'usd',
  //           environment
  //         )
  //         const data = await response.json()
  //
  //         if (data) {
  //           feeCurrency = pair.liquidityToken
  //           feeCurrencyId = pair.liquidityToken.address.toLowerCase()
  //           decimals = BigNumber.from(pair.liquidityToken.decimals)
  //         }
  //       } catch (e) {
  //         feeCurrency = wrappedTokenA
  //         feeCurrencyId = wrappedTokenA.address
  //         decimals = BigNumber.from(wrappedTokenA.decimals)
  //         console.info(
  //           'The corresponding LP token cannot be used as a fee token due to unavailable price data. Fee token will revert to input token.',
  //           e
  //         )
  //       }
  //     } else if (userUseCustomFeeToken && userFeeCurrency) {
  //       feeCurrency = userFeeCurrency
  //       feeCurrencyId = userFeeCurrency.wrapped.address
  //       decimals = BigNumber.from(userFeeCurrency.decimals)
  //     } else {
  //       feeCurrency = wrappedTokenA
  //       feeCurrencyId = wrappedTokenA.address
  //       decimals = BigNumber.from(wrappedTokenA.decimals)
  //     }
  //
  //     // const feeCurrencyId =
  //     //   userUseCustomFeeToken && userFeeCurrency
  //     //     ? userFeeCurrency.wrapped.address
  //     //     : pair && pair.liquidityToken.address !== undefined
  //     //     ? pair.liquidityToken.address.toLowerCase()
  //     //     : wrappedTokenA.address
  //
  //     let maxTokenFee = BigNumber.from(0)
  //     switch (chainId) {
  //       case ChainId.MATIC:
  //       case ChainId.MOONRIVER:
  //         maxTokenFee = await calculateFeeThenConvert(
  //           chainId,
  //           feeCurrencyId,
  //           decimals,
  //           txnFee,
  //           environment
  //         )
  //         break
  //       case ChainId.BSC:
  //         maxTokenFee = await calculateFee(
  //           chainId,
  //           feeCurrencyId,
  //           decimals,
  //           txnFee,
  //           'bnb',
  //           NATIVE[ChainId.BSC].decimals,
  //           environment
  //         )
  //         break
  //       case ChainId.ARBITRUM:
  //         maxTokenFee = await calculateFee(
  //           chainId,
  //           feeCurrencyId,
  //           decimals,
  //           txnFee,
  //           'eth',
  //           NATIVE[ChainId.ARBITRUM].decimals,
  //           environment
  //         )
  //         break
  //     }
  //
  //     dispatch(addLiquitityFeeInTokens(maxTokenFee.toString()))
  //     dispatch(feeTokenSymbol(feeCurrency?.symbol))
  //   }
  // })

  return {
    dependentField,
    currencies,
    pair,
    pairState,
    currencyBalances,
    parsedAmounts,
    // parsedOriginalAmounts,
    price,
    noLiquidity,
    liquidityMinted,
    poolTokenPercentage,
    error
    // feeCurrency
  }
}
